import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../hooks/useAuth';
import '../styles/MfaAdminDashboard.css';

const MfaAdminDashboard = () => {
  const { hasPermission } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [unlockingUser, setUnlockingUser] = useState(null);
  const [forceLoggingOut, setForceLoggingOut] = useState(null); // session_id being terminated

  // Check permission
  if (!hasPermission('/mfa-admin-dashboard', 'View')) {
    return (
      <div className="mfa-admin-dashboard">
        <div className="access-denied">
          <h2>Access Denied</h2>
          <p>You don't have permission to view the MFA Admin Dashboard.</p>
        </div>
      </div>
    );
  }

  useEffect(() => {
    fetchMfaStatus();
  }, []);

  const fetchMfaStatus = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/mfa/admin/status`
      );
      setData(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load MFA status');
    } finally {
      setLoading(false);
    }
  };

  const handleUnlockUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to unlock ${userName}?`)) {
      return;
    }

    setUnlockingUser(userId);
    try {
      await axios.post(
        `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/mfa/admin/unlock-user`,
        { userId }
      );
      
      // Refresh data
      await fetchMfaStatus();
      alert(`${userName} has been unlocked successfully.`);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to unlock user');
    } finally {
      setUnlockingUser(null);
    }
  };

  const handleForceLogout = async (userId, userName, sessionId) => {
    const scopeMsg = sessionId ? `this device session` : `ALL sessions`;
    if (!window.confirm(`Force log out ${userName} from ${scopeMsg}? They will be signed out immediately on their next request.`)) {
      return;
    }

    setForceLoggingOut(sessionId || userId);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/mfa/admin/force-logout`,
        { userId, sessionId }
      );
      await fetchMfaStatus();
      alert(response.data.message);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to force logout user');
    } finally {
      setForceLoggingOut(null);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const getStatusBadge = (status) => {
    const colors = {
      pending: 'badge-warning',
      verified: 'badge-success',
      expired: 'badge-secondary',
      locked: 'badge-danger'
    };
    return <span className={`badge ${colors[status] || 'badge-secondary'}`}>{status}</span>;
  };

  const getVerificationBadge = (status) => {
    const colors = {
      Verified: 'badge-success',
      'Not Verified': 'badge-warning',
      Expired: 'badge-danger'
    };
    return <span className={`badge ${colors[status] || 'badge-secondary'}`}>{status}</span>;
  };

  const getMinutesUntilExpiry = (expiresAt) => {
    const now = new Date();
    const expiry = new Date(expiresAt);
    const minutes = Math.ceil((expiry - now) / (1000 * 60));
    return Math.max(0, minutes);
  };

  /** Parse a user-agent string into a short, friendly device label. */
  const getDeviceName = (ua) => {
    if (!ua) return 'Unknown Device';
    // Mobile devices
    if (/iPad/.test(ua)) return '📱 iPad';
    if (/iPhone/.test(ua)) return '📱 iPhone';
    if (/Android.*Mobile/.test(ua)) return '📱 Android Phone';
    if (/Android/.test(ua)) return '📱 Android Tablet';
    // Desktop OS
    if (/Macintosh|Mac OS X/.test(ua)) return '💻 Mac';
    if (/Windows/.test(ua)) return '💻 Windows PC';
    if (/Linux/.test(ua)) return '💻 Linux PC';
    if (/CrOS/.test(ua)) return '💻 Chromebook';
    return '🖥 Unknown Device';
  };

  if (loading) {
    return (
      <div className="mfa-admin-dashboard">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading MFA status...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mfa-admin-dashboard">
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="mfa-admin-dashboard">
      <div className="dashboard-header">
        <h1>🔐 MFA Administration Dashboard</h1>
        <button onClick={fetchMfaStatus} className="refresh-btn">
          🔄 Refresh
        </button>
      </div>

      {/* Summary Cards */}
      <div className="summary-cards">
        <div className="summary-card">
          <div className="card-icon">👥</div>
          <div className="card-content">
            <h3>{data.summary.totalMfaUsers}</h3>
            <p>Users with MFA</p>
          </div>
        </div>
        <div className="summary-card">
          <div className="card-icon">🟢</div>
          <div className="card-content">
            <h3>{data.summary.activeSessions}</h3>
            <p>Active Sessions</p>
          </div>
        </div>
        <div className="summary-card alert">
          <div className="card-icon">🔒</div>
          <div className="card-content">
            <h3>{data.summary.lockedUsers}</h3>
            <p>Locked Users</p>
          </div>
        </div>
        <div className="summary-card">
          <div className="card-icon">📊</div>
          <div className="card-content">
            <h3>{data.summary.recentAttempts}</h3>
            <p>Recent Attempts (24h)</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs">
        <button
          className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button
          className={`tab ${activeTab === 'sessions' ? 'active' : ''}`}
          onClick={() => setActiveTab('sessions')}
        >
          Active Sessions
        </button>
        <button
          className={`tab ${activeTab === 'locked' ? 'active' : ''}`}
          onClick={() => setActiveTab('locked')}
        >
          Locked Users ({data.lockedCodes.length})
        </button>
        <button
          className={`tab ${activeTab === 'recent' ? 'active' : ''}`}
          onClick={() => setActiveTab('recent')}
        >
          Recent Activity
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'overview' && (
          <div className="overview-tab">
            <h2>MFA-Enabled Users</h2>
            <table className="data-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Verification</th>
                  <th>Valid Until</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {data.users.map(user => (
                  <tr key={user.id}>
                    <td>{user.id}</td>
                    <td>{user.name}</td>
                    <td>{user.email}</td>
                    <td><span className="badge badge-info">{user.role}</span></td>
                    <td>{getVerificationBadge(user.mfaVerificationStatus)}</td>
                    <td>{user.mfaValidUntil ? formatDate(user.mfaValidUntil) : '—'}</td>
                    <td>
                      <button
                        className="force-logout-btn"
                        onClick={() => handleForceLogout(user.id, user.name, null)}
                        disabled={forceLoggingOut === user.id}
                        title="Terminate all active sessions for this user"
                      >
                        {forceLoggingOut === user.id ? 'Logging out…' : '⏏ All Devices'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'sessions' && (
          <div className="sessions-tab">
            <h2>Active Sessions with MFA Info</h2>
            <table className="data-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Session ID</th>
                  <th>Device</th>
                  <th>IP Address</th>
                  <th>Last Activity</th>
                  <th>MFA Verified At</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {data.activeSessions.map(session => (
                  <tr key={session.session_id}>
                    <td>
                      <div className="user-info">
                        <strong>{session.User?.name || 'Unknown'}</strong>
                        <small>{session.User?.email}</small>
                      </div>
                    </td>
                    <td><code className="session-id">{session.session_id.substring(0, 8)}...</code></td>
                    <td title={session.user_agent || ''}>{getDeviceName(session.user_agent)}</td>
                    <td>{session.ip_address}</td>
                    <td>{formatDate(session.last_activity)}</td>
                    <td>
                      {session.last_mfa_verified_at ? (
                        <span className="verified">✓ {formatDate(session.last_mfa_verified_at)}</span>
                      ) : (
                        <span className="not-verified">Not verified</span>
                      )}
                    </td>
                    <td>
                      <button
                        className="force-logout-btn"
                        onClick={() => handleForceLogout(session.user_id, session.User?.name || 'User', session.session_id)}
                        disabled={forceLoggingOut === session.session_id}
                        title="Terminate this device session immediately"
                      >
                        {forceLoggingOut === session.session_id ? 'Logging out…' : '⏏ Force Logout'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'locked' && (
          <div className="locked-tab">
            <h2>Locked Users</h2>
            {data.lockedCodes.length === 0 ? (
              <div className="empty-state">
                <p>✓ No users are currently locked</p>
              </div>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Lock Reason</th>
                    <th>Attempts</th>
                    <th>Locked At</th>
                    <th>Expires At</th>
                    <th>Time Remaining</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.lockedCodes.map(code => (
                    <tr key={code.id}>
                      <td>
                        <div className="user-info">
                          <strong>{code.User?.name || 'Unknown'}</strong>
                          <small>{code.User?.email}</small>
                        </div>
                      </td>
                      <td>
                        {code.trigger_reason === 'rate_limit' ? (
                          <span className="badge badge-warning">Too many requests</span>
                        ) : (
                          <span className="badge badge-danger">Failed verification</span>
                        )}
                      </td>
                      <td>
                        <span className="badge badge-danger">
                          {code.trigger_reason === 'rate_limit'
                            ? `${code.attempts}/3`
                            : `${code.attempts}/5`}
                        </span>
                      </td>
                      <td>{formatDate(code.created_at)}</td>
                      <td>{formatDate(code.expires_at)}</td>
                      <td>
                        <span className="time-remaining">
                          {getMinutesUntilExpiry(code.expires_at)} minutes
                        </span>
                      </td>
                      <td>
                        <button
                          className="unlock-btn"
                          onClick={() => handleUnlockUser(code.user_id, code.User?.name)}
                          disabled={unlockingUser === code.user_id}
                        >
                          {unlockingUser === code.user_id ? 'Unlocking...' : '🔓 Unlock'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {activeTab === 'recent' && (
          <div className="recent-tab">
            <h2>Recent MFA Activity (Last 24 Hours)</h2>
            <table className="data-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Status</th>
                  <th>Attempts</th>
                  <th>Created At</th>
                  <th>Verified At</th>
                  <th>IP Address</th>
                  <th>Trigger Reason</th>
                </tr>
              </thead>
              <tbody>
                {data.recentCodes.map(code => (
                  <tr key={code.id}>
                    <td>
                      <div className="user-info">
                        <strong>{code.User?.name || 'Unknown'}</strong>
                        <small>{code.User?.email}</small>
                      </div>
                    </td>
                    <td>{getStatusBadge(code.status)}</td>
                    <td>
                      <span className={`attempts ${code.attempts >= 3 ? 'high' : ''}`}>
                        {code.attempts}/5
                      </span>
                    </td>
                    <td>{formatDate(code.created_at)}</td>
                    <td>
                      {code.verified_at ? (
                        formatDate(code.verified_at)
                      ) : (
                        <span className="not-verified">—</span>
                      )}
                    </td>
                    <td>{code.ip_address}</td>
                    <td>
                      <span className="trigger-reason">{code.trigger_reason || '—'}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default MfaAdminDashboard;
